import React, { useState } from 'react';
import {
    Box,
    Card,
    CardMedia,
    Typography,
    Dialog,
    DialogContent,
    IconButton,
    Skeleton
} from '@mui/material';
import {
    Close as CloseIcon,
    ZoomIn as ZoomIcon
} from '@mui/icons-material';

const Screenshot = ({
    src,
    alt,
    caption,
    width = '100%',
    height = 'auto',
    clickable = true,
    placeholder = true
}) => {
    const [open, setOpen] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    const handleOpen = () => {
        if (clickable && !imageError) {
            setOpen(true);
        }
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleImageLoad = () => {
        setImageLoaded(true);
    };

    const handleImageError = () => {
        setImageError(true);
        setImageLoaded(true);
    };

    // If no src provided, show placeholder
    if (!src || imageError) {
        return (
            <Box sx={{ mb: 2 }}>
                <Card
                    sx={{
                        border: '2px dashed',
                        borderColor: 'grey.300',
                        backgroundColor: 'grey.50',
                        cursor: 'default'
                    }}
                >
                    <Box
                        sx={{
                            height: 200,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            p: 3
                        }}
                    >
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            📸 Screenshot Placeholder
                        </Typography>
                        <Typography variant="body2" color="text.secondary" align="center">
                            {alt || 'Screenshot will be added here'}
                        </Typography>
                    </Box>
                </Card>
                {caption && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {caption}
                    </Typography>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 2 }}>
            <Card
                sx={{
                    cursor: clickable ? 'pointer' : 'default',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': clickable ? {
                        transform: 'scale(1.02)',
                        boxShadow: 3
                    } : {},
                    position: 'relative'
                }}
                onClick={handleOpen}
            >
                {!imageLoaded && (
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={200}
                        animation="wave"
                    />
                )}
                <CardMedia
                    component="img"
                    image={src}
                    alt={alt}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    sx={{
                        width,
                        height,
                        display: imageLoaded ? 'block' : 'none'
                    }}
                />
                {clickable && imageLoaded && !imageError && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            borderRadius: '50%',
                            p: 0.5,
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            '.MuiCard-root:hover &': {
                                opacity: 1
                            }
                        }}
                    >
                        <ZoomIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                )}
            </Card>

            {caption && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {caption}
                </Typography>
            )}

            {/* Full-size dialog */}
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogContent sx={{ p: 0, position: 'relative' }}>
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            color: 'white',
                            zIndex: 1,
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.9)'
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <img
                        src={src}
                        alt={alt}
                        style={{
                            width: '100%',
                            height: 'auto',
                            display: 'block'
                        }}
                    />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default Screenshot;